import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import Dialog, { DialogActionBar } from 'src/components/common/Dialog.redesign';
import FormButton from 'src/components/controls/forms/redesign/FormButton';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';
import ProvisionClauseType from 'src/models/ProvisionClauseType';
import useFetch from 'use-http';
import * as Constants from 'src/components/common/Constants';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import AgreementUtil from 'src/util/AgreementUtil';

export interface IProvisionClauseTypeDialogProps {
    clientNumber: number;
    projectNumber: number;
    provisionClauseType: ProvisionClauseType;
    provisionValues: IndexReferenceType[];
    provisionUnits: IndexReferenceType[];
    onSave: (updated: ProvisionClauseType) => void;
    onCloseDialog?: () => void;
}

const ProvisionClauseTypeDialog = (props: IProvisionClauseTypeDialogProps) => {
    const { post, put, response } = useFetch();

    const [values, setValues] = useState<IndexReferenceType[]>(props.provisionClauseType.provisionValues ?? []);
    const [units, setUnits]   = useState<IndexReferenceType[]>(props.provisionClauseType.provisionUnits  ?? []);

    // Single input per column — drives both catalog filtering and free-text add
    const [valueInput, setValueInput] = useState('');
    const [unitInput,  setUnitInput]  = useState('');
    const [typeInput, setTypeInput] = useState('');
    const [contractTypeInput, setContractTypeInput] = useState<number>(undefined);
    const [omitObligation, setOmitObligation] = useState<string>('false');

    // Catalog suggestions: only shown when input is non-empty
    const suggestedValues = valueInput.trim()
        ? props.provisionValues.filter(cv => !values.some(v => v.name === cv.name) && cv.name.toLowerCase().includes(valueInput.toLowerCase()))
        : [];
    const suggestedUnits = unitInput.trim()
        ? props.provisionUnits.filter(cu => !units.some(u => u.name === cu.name) && cu.name.toLowerCase().includes(unitInput.toLowerCase()))
        : [];

    useEffect(() => {
        if (!!props?.provisionClauseType) {
            setOmitObligation(props.provisionClauseType.omitObligation?.toString() ?? 'false');
            if (props.provisionClauseType.contractTypeId > 0) {
                setContractTypeInput(props.provisionClauseType.contractTypeId);
            }
        }
    }, []);

    // ── Values handlers ────────────────────────────────────────────────────────
    const addValue = () => {
        if (!valueInput.trim()) { return; }
        setValues(prev => _.uniqBy([...prev, { name: valueInput.trim().toUpperCase() } as IndexReferenceType], 'name'));
        setValueInput('');
    };
    const removeValue = (name: string) => setValues(prev => prev.filter(v => v.name !== name));
    const addValueFromCatalog = (item: IndexReferenceType) => {
        setValues(prev => _.uniqBy([...prev, item], 'name'));
        setValueInput('');
    };

    // ── Units handlers ─────────────────────────────────────────────────────────
    const addUnit = () => {
        if (!unitInput.trim()) { return; }
        setUnits(prev => _.uniqBy([...prev, { name: unitInput.trim().toUpperCase() } as IndexReferenceType], 'name'));
        setUnitInput('');
    };
    const removeUnit = (name: string) => setUnits(prev => prev.filter(u => u.name !== name));
    const addUnitFromCatalog = (item: IndexReferenceType) => {
        setUnits(prev => _.uniqBy([...prev, item], 'name'));
        setUnitInput('');
    };

    // ── Save / Cancel ──────────────────────────────────────────────────────────
    const handleSave = () => {
        if (contractTypeInput > 0) {
            let pType = props.provisionClauseType;
            if (pType.name === undefined && typeInput.length === 0) {
                window.alert('A name for the new Provision type is required!');
                return;
            } else if (typeInput.length > 0) {
                pType.name = (props.provisionClauseType.name ?? typeInput).toUpperCase();
            }
    
            pType.contractTypeId = contractTypeInput;
            pType.provisionValues = values;
            pType.provisionUnits = units;
            pType.omitObligation = AgreementUtil.parseBool(omitObligation);
    
            if (pType.id > 0) {
                addRemoveAttachments(pType);
            } else {
                createProvisionType(pType);
            }
        } else {
            window.alert('Contract Type required!');
        }
    };

    const addRemoveAttachments = async (clauseType: ProvisionClauseType) => {
        let result = await put(`/v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/provisionLeaseClause/types`, clauseType);

        if (response.ok) {
            props.onSave(clauseType);
            props.onCloseDialog();
        } else {
            console.log('Unable to update Provision Type!' + result);
        }
    };

    const createProvisionType = async (clauseType: ProvisionClauseType) => {
        let result = await post(`/v1.0/clients/${props.clientNumber}/projects/${props.projectNumber}/provisionLeaseClause/types`, clauseType);

        if (response.ok) {
            props.onSave(clauseType);
            props.onCloseDialog();
        } else {
            console.log('Unable to create Provision Type!' + result);
        }
    };

    // ── Shared list-panel renderer ─────────────────────────────────────────────
    const renderPanel = (
        label: string,
        items: IndexReferenceType[],
        onRemove: (name: string) => void,
        inputValue: string,
        onInputChange: (val: string) => void,
        onAdd: () => void,
        suggestions: IndexReferenceType[],
        onAddFromCatalog: (item: IndexReferenceType) => void
    ) => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Current list — flex: 1 so both columns grow to the same height */}
            <fieldset style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginBottom: '1rem', display: 'flex', flexDirection: 'column', backgroundColor: 'white' }}>
                <legend style={{ fontWeight: 'bold', padding: '0 .4rem' }}>{label}</legend>
                {items.length === 0 && (
                    <div style={{ color: '#aaa', padding: '.4rem .6rem', fontSize: '1.3rem' }}>None</div>
                )}
                {items.map(item => (
                    <div
                        key={item.name}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '.2rem .6rem' }}
                    >
                        <span>{item.name}</span>
                        <span
                            onClick={() => onRemove(item.name)}
                            style={{ cursor: 'pointer', color: '#c23030', marginLeft: '1rem', fontWeight: 'bold' }}
                            title="Remove"
                        >
                            x
                        </span>
                    </div>
                ))}
            </fieldset>

            {/* Combined search + add input */}
            <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem', alignItems: 'center', flexShrink: 0 }}>
                <input
                    value={inputValue}
                    onChange={e => onInputChange(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && onAdd()}
                    placeholder={`Search or add new ${label.slice(0, -1).toLowerCase()}...`}
                    style={{ flex: 1, padding: '.4rem .6rem', fontSize: '1.4rem' }}
                />
                <FormButton text="Add" onClick={onAdd} size="small" intent="primary" />
            </div>

            {/* Catalog suggestions — fixed height, empty until user types */}
            <fieldset style={{ height: '14rem', overflowY: 'auto', flexShrink: 0, backgroundColor: 'white' }}>
                <legend style={{ fontWeight: 'bold', padding: '0 .4rem' }}>Search</legend>
                {inputValue.trim() && suggestions.length === 0 && (
                    <div style={{ color: '#aaa', fontSize: '1.3rem', padding: '.2rem .6rem' }}>
                        No catalog matches — press Add to create new
                    </div>
                )}
                {suggestions.map(item => (
                    <div
                        key={item.name}
                        onClick={() => onAddFromCatalog(item)}
                        style={{ padding: '.2rem .6rem', cursor: 'pointer', fontSize: '1.4rem' }}
                    >
                        {item.name}
                    </div>
                ))}
            </fieldset>
        </div>
    );

    const typesPanel = () => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {!props.provisionClauseType?.id && (
                <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem', alignItems: 'center', flexShrink: 0 }}>
                    <RHFLegend title="Provision Type" />
                    <input
                        value={typeInput}
                        onChange={e => setTypeInput(e.target.value)}
                        placeholder={`Add new provision type...`}
                        style={{ flex: 1, padding: '.4rem .6rem', fontSize: '1.4rem' }}
                    />
                </div>
            )}
            <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem', alignItems: 'center', flexShrink: 0 }}>
                <RHFSelect
                    defaultVal={props?.provisionClauseType?.contractTypeId}
                    label="Contract Type"
                    name="agreementType"
                    onChange={(e) => setContractTypeInput(Number(e.currentTarget.value))}
                    options={Object.values(Constants.contractTypeRef).map((a) => {
                        return { value: a.value, label: a.label };
                    })}
                />
                <RHFToggle 
                    defaultValue={props?.provisionClauseType?.omitObligation?.toString() ?? 'false'}
                    name="omitObligation" 
                    label="Omit Obligation?"
                    onChange={(e) => setOmitObligation(e.currentTarget.value)}
                />
            </div>
        </div>
    );

    return (
        <Dialog title={props.provisionClauseType.name?.toUpperCase() ?? 'ADD OR CREATE NEW PROVISION'}>
            <div style={{ width: '50vw' }}>
                {typesPanel()}
                <div style={{ display: 'flex', gap: '2rem', height: 'calc(80vh - 14rem)' }}>
                    {renderPanel(
                        'Values',
                        values,
                        removeValue,
                        valueInput,
                        setValueInput,
                        addValue,
                        suggestedValues,
                        addValueFromCatalog
                    )}
                    {renderPanel(
                        'Units',
                        units,
                        removeUnit,
                        unitInput,
                        setUnitInput,
                        addUnit,
                        suggestedUnits,
                        addUnitFromCatalog
                    )}
                </div>
            </div>

            <DialogActionBar>
                <FormButton text="Save" intent="primary" onClick={handleSave} />
                <FormButton text="Cancel" onClick={props.onCloseDialog} />
            </DialogActionBar>
        </Dialog>
    );
};

export default ProvisionClauseTypeDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/ProvisionClauseTypeDialog.tsx